/**
 * TUVI - ContratistaPDF
 * Plantilla PDF para Contratistas (copia adaptada de CotizacionPDF)
 * Cambios vs CotizacionPDF:
 * - "COTIZACIÓN" → "ESPECIFICACIONES"
 * - Sin fecha cotización, fecha entrega, validez oferta
 * - Sin forma cocina ni baño constructora
 * - Agrega sección de planos (múltiples imágenes por recuadro)
 * - Actividades agrupadas por capítulo (no por espacio)
 * - Datos Residentes a cargo (editable) en vez de Datos Propietario
 * - Observaciones y Consideraciones de Ficha FFE
 * - Footer: final_contratistas.jpg
 */

import React, { forwardRef } from 'react';
import { BACKEND_URL } from '../../config';
import './Cotizaciones.css';

// Observaciones fijas (mismas de Ficha FFE)
const OBSERVACIONES = [
  'El presente documento corresponde a la ficha de especificaciones técnicas del proyecto y resume los materiales, referencias, cantidades y alcances aprobados durante la fase de diseño y presupuesto.',
  'Con la firma de aprobación de este documento, el cliente confirma que ha revisado y está de acuerdo con las especificaciones aquí descritas.',
  'Todo cambio posterior a la firma (modificación de referencias, cantidades, materiales o decisiones de diseño) podrá generar variación en tiempos de entrega y costo final. Estos adicionales, deberán enviarse por escrito y solo se ejecutarán después de su aprobación formal y ajuste económico correspondiente.',
  'Todos los adicionales solicitados por el contratante se deben dejar por escrito y firmado previo a la ejecución de obra.',
  'Una vez firmado este documento, se procederá con el pago del anticipo, necesario para asignar el ingeniero residente de obra y asegurar la disponibilidad de materiales y cronograma.',
  'Después de recibido el anticipo, el ingeniero residente entregará el cronograma oficial de ejecución.',
  'Las especificaciones de iluminación, traslados, nichos, buitrones y movimientos de redes estarán sujetas a validación en sitio durante la semana preoperativa, realizada por el Ingeniero Residente. Cualquier variación o condición técnica identificada será informada oportunamente al cliente antes de su ejecución.',
  'Este documento garantiza transparencia y trazabilidad en la ejecución del proyecto, buscando proteger los intereses del cliente y asegurar los estándares de calidad ofrecidos por Tuvi.'
];

// URL del proxy para Google Drive
const getProxyUrl = (url) => {
  if (!url) return null;
  if (url.includes('/upload/catalogo/') || url.includes('api.tuvisistema.com')) return url;
  return `${BACKEND_URL}/api/documentos/proxy-imagen.php?url=${encodeURIComponent(url)}`;
};

const ContratistaPDF = forwardRef(({ cotizacion, actividades, planos = [], consideraciones = '', residente = {} }, ref) => {
  if (!cotizacion) return null;

  const {
    proyecto_nombre, apartamento, area_m2, habitaciones, banos
  } = cotizacion;

  // Normalizar (quitar tildes y uppercase) — para detectar capítulos robusto
  const normalizar = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  // Agrupar actividades FFE por capítulo (fallback a categoría si no hay capítulo)
  const actividadesPorCapitulo = {};
  (actividades || []).forEach(act => {
    const cap = normalizar(act.capitulo || act.categoria || 'GENERALES');
    if (!actividadesPorCapitulo[cap]) {
      actividadesPorCapitulo[cap] = [];
    }
    actividadesPorCapitulo[cap].push(act);
  });

  const capitulosOrdenados = Object.keys(actividadesPorCapitulo);
  let itemGlobal = 0;

  return (
    <div ref={ref}>
      <div className="contratista-page">
        {/* Banner */}
        <div className="pdf-banner">
          <img src="/logo_tuvi_banner.jpg" alt="TUVI" className="pdf-banner-logo" crossOrigin="anonymous" />
          <span className="pdf-banner-title">ESPECIFICACIONES</span>
        </div>

        {/* Contenido */}
        <div style={{ padding: '65px 65px 0' }}>
          {/* Datos Residentes a cargo e Inmueble - 2 columnas */}
          <div className="pdf-datos-wrapper">
            {/* Columna izquierda: Residentes a cargo */}
            <div className="pdf-datos-left">
              <table className="pdf-datos-block">
                <thead>
                  <tr>
                    <th colSpan="2" className="pdf-datos-title-dark">Datos Residentes a cargo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pdf-dato-lbl">Nombre</td>
                    <td className="pdf-dato-val">{residente.nombre || ''}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Contacto</td>
                    <td className="pdf-dato-val">{residente.contacto || ''}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Empresa</td>
                    <td className="pdf-dato-val">{residente.empresa || ''}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Columna derecha: Inmueble */}
            <div className="pdf-datos-right">
              <table className="pdf-datos-block">
                <thead>
                  <tr>
                    <th colSpan="4" className="pdf-datos-title-dark">Datos del Inmueble</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pdf-dato-lbl">Proyecto</td>
                    <td colSpan="3" className="pdf-dato-val">{proyecto_nombre}</td>
                  </tr>
                  <tr className="pdf-separator-row"><td colSpan="4"></td></tr>
                  <tr>
                    <td className="pdf-dato-lbl">Area</td>
                    <td className="pdf-dato-val">{area_m2}</td>
                    <td className="pdf-dato-lbl">Habitaciones</td>
                    <td className="pdf-dato-val">{habitaciones}</td>
                  </tr>
                  <tr>
                    <td className="pdf-dato-lbl">Baños</td>
                    <td className="pdf-dato-val">{banos}</td>
                    <td className="pdf-dato-lbl">Apartamento</td>
                    <td className="pdf-dato-val">{apartamento}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Planos (múltiples imágenes por recuadro) */}
          {planos.length > 0 && (
            <div className="cotizacion-pdf-section" style={{ marginTop: '8px' }}>
              <h4 className="pdf-section-beige">PLANOS</h4>
              <div className="pdf-planos-contratista-grid">
                {planos.map((plano, idx) => (
                  <div key={idx} className={`pdf-plano-contratista${(plano.urls && plano.urls.filter(u => u).length > 1) ? ' pdf-plano-contratista-wide' : ''}`}>
                    <div className="pdf-plano-contratista-header">{plano.titulo || `PLANO ${idx + 1}`}</div>
                    <div className={`pdf-plano-contratista-body${(plano.urls && plano.urls.filter(u => u).length > 1) ? ' pdf-plano-body-grid' : ''}`}>
                      {(plano.urls && plano.urls.length > 0) ? (
                        plano.urls.filter(u => u).map((url, uIdx) => (
                          <img
                            key={uIdx}
                            src={getProxyUrl(url)}
                            alt={`${plano.titulo} ${uIdx + 1}`}
                            className="pdf-plano-contratista-img"
                            crossOrigin="anonymous"
                          />
                        ))
                      ) : plano.url ? (
                        <img
                          src={getProxyUrl(plano.url)}
                          alt={plano.titulo}
                          className="pdf-plano-contratista-img"
                          crossOrigin="anonymous"
                        />
                      ) : (
                        <div className="pdf-plano-contratista-empty"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ACTIVIDADES POR CAPÍTULO */}
          <div className="cotizacion-pdf-section" style={{ marginTop: '10px', marginBottom: '5px' }}>
            <h4 className="pdf-section-beige">ACTIVIDADES DE ESPECIFICACIONES</h4>
          </div>
          <table className="pdf-actividades-table">
            <thead>
              <tr>
                <th style={{ width: '30px' }}>Item</th>
                <th>Descripcion</th>
                <th style={{ width: '35px' }}>Und</th>
                <th style={{ width: '35px' }}>Cant</th>
              </tr>
            </thead>
            <tbody>
              {capitulosOrdenados.map((capitulo, gIdx) => {
                const acts = actividadesPorCapitulo[capitulo] || [];
                const rows = [];
                const esCarpinteria = capitulo.includes('CARPINTERIA'); // capitulo ya está normalizado (sin tildes, uppercase)
                if (gIdx > 0) {
                  rows.push(
                    <tr key={`sep1-${gIdx}`} className="pdf-espacio-separator"><td colSpan="4"></td></tr>,
                    <tr key={`sep2-${gIdx}`} className="pdf-espacio-separator"><td colSpan="4"></td></tr>
                  );
                }
                rows.push(
                  <tr key={`cap-${gIdx}`} className="espacio-header">
                    <td colSpan="4">{capitulo}</td>
                  </tr>
                );
                acts.forEach((act, aIdx) => {
                  itemGlobal++;
                  rows.push(
                    <tr key={`act-${gIdx}-${aIdx}`} className={aIdx % 2 === 1 ? 'pdf-act-row-beige' : ''}>
                      <td>{itemGlobal}</td>
                      <td>{act.descripcion}</td>
                      <td>{act.unidad}</td>
                      <td>{(act.unidad === 'UND' || (act.unidad === 'ML' && esCarpinteria)) ? act.cantidad : ''}</td>
                    </tr>
                  );
                });
                return rows;
              })}
            </tbody>
          </table>
          {/* Observaciones */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">OBSERVACIONES</h4>
            <div className="pdf-observaciones">
              {OBSERVACIONES.map((obs, idx) => (
                <p key={idx}>{idx + 1}. {obs}</p>
              ))}
            </div>
          </div>

          {/* Consideraciones especiales */}
          <div className="cotizacion-pdf-section pdf-section-small" style={{ marginTop: '10px' }}>
            <h4 className="pdf-section-beige">CONSIDERACIONES ESPECIALES</h4>
            <div className="pdf-consideraciones">
              <p>{consideraciones || '*En este proyecto no se identifican consideraciones especiales.'}</p>
            </div>
          </div>

        </div>

        {/* Imagen final */}
        <div className="pdf-imagen-final">
          <img src="/final_contratistas.png" alt="TUVI" crossOrigin="anonymous" />
        </div>
      </div>
    </div>
  );
});

ContratistaPDF.displayName = 'ContratistaPDF';

export default ContratistaPDF;
